import { useEffect, useState } from 'react';
import { axiosInstance } from '../../lib/api-client';
import { Clock, DollarSign, CheckCircle, XCircle, AlertCircle, Eye, TrendingUp, User, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { CreatePromotionalOrderForm } from '../CreatePromotionalOrderForm';
import { logger } from '../../utils/logger';
import { PaginationWrapper } from '../ui/PaginationWrapper';

interface PromotionalOrder {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  planId: {
    name: string;
    price: number;
    duration: number;
  };
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  originalPrice: number;
  discountedPrice: number;
  discountPercentage: number;
  extraContacts: number;
  extraValidity: number;
  status: 'pending' | 'completed' | 'expired' | 'cancelled';
  validUntil: string;
  createdAt: string;
  commissionEarned: number;
  commissionPercentage: number;
  commissionPaid: boolean;
  viewCount: number;
  campaignName: string;
  notes: string;
  paymentId?: string;
  paymentDate?: string;
}

interface Stats {
  total: number;
  pending: number;
  completed: number;
  expired: number;
  totalRevenue: number;
  totalCommissions: number;
  paidCommissions: number;
  pendingCommissions: number;
}

export function AdminPromotionalOrders() {
  const [orders, setOrders] = useState<PromotionalOrder[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const limit = 20;
  const [selectedOrder, setSelectedOrder] = useState<PromotionalOrder | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [selectedStatus, page]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const statusParam = selectedStatus !== 'all' ? `&status=${selectedStatus}` : '';
      
      const response = await axiosInstance.get(
        `/promotional-orders?page=${page}&limit=${limit}${statusParam}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setOrders(response.data.data.orders);
        setTotalPages(response.data.data.pagination.totalPages);
        setTotalItems(response.data.data.pagination.total);
      }
    } catch (error: any) {
      logger.error('Error fetching promotional orders:', error);
      toast.error('Failed to fetch promotional orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance.get(
        `/promotional-orders/stats`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error: any) {
      logger.error('Error fetching stats:', error);
    }
  };

  const markCommissionAsPaid = async (orderId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axiosInstance.patch(
        `/promotional-orders/${orderId}/mark-commission-paid`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Commission marked as paid');
      fetchOrders();
      fetchStats();
    } catch (error: any) {
      logger.error('Error marking commission as paid:', error);
      toast.error('Failed to mark commission as paid');
    }
  };

  const cancelOrder = async (orderId: string) => {
    if (!confirm('Are you sure you want to cancel this promotional order?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axiosInstance.delete(
        `/promotional-orders/${orderId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Promotional order cancelled successfully');
      fetchOrders();
      fetchStats();
      if (showDetailsModal) {
        setShowDetailsModal(false);
      }
    } catch (error: any) {
      logger.error('Error cancelling promotional order:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel promotional order');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>,
      completed: <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" /> Completed</Badge>,
      expired: <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100"><AlertCircle className="w-3 h-3 mr-1" /> Expired</Badge>,
      cancelled: <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><XCircle className="w-3 h-3 mr-1" /> Cancelled</Badge>
    };
    return badges[status as keyof typeof badges] || status;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewDetails = (order: PromotionalOrder) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Promotional Orders</h2>
        <Button
          onClick={() => setShowCreateOrderModal(true)}
          className="bg-rose-500 hover:bg-rose-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Promotional Order
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <div className="flex gap-2 mt-2 text-xs">
                  <span className="text-yellow-600">{stats.pending} pending</span>
                  <span className="text-green-600">{stats.completed} completed</span>
                </div>
              </div>
              <TrendingUp className="w-10 h-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600">₹{stats.totalRevenue.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">From completed orders</p>
              </div>
              <DollarSign className="w-10 h-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Commissions</p>
                <p className="text-2xl font-bold text-orange-600">₹{stats.totalCommissions.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">₹{stats.paidCommissions.toFixed(2)} paid</p>
              </div>
              <DollarSign className="w-10 h-10 text-orange-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Commissions</p>
                <p className="text-2xl font-bold text-red-600">₹{stats.pendingCommissions.toFixed(2)}</p>
                <p className="text-xs text-gray-500 mt-1">To be paid to marketing</p>
              </div>
              <Clock className="w-10 h-10 text-red-500" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'completed', 'expired', 'cancelled'].map(status => (
            <button
              key={status}
              onClick={() => {
                setSelectedStatus(status);
                setPage(1);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedStatus === status
                  ? 'bg-rose-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading...</div>
        ) : orders.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No promotional orders found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pricing</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">
                            {order.userId.firstName} {order.userId.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{order.userId.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">
                            {order.createdBy.firstName} {order.createdBy.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{order.createdBy.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{order.planId.name}</div>
                        {order.extraContacts > 0 && (
                          <div className="text-xs text-green-600">+{order.extraContacts} contacts</div>
                        )}
                        {order.extraValidity > 0 && (
                          <div className="text-xs text-blue-600">+{order.extraValidity} days</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <span className="text-sm line-through text-gray-400">₹{order.originalPrice}</span>
                          <span className="ml-2 font-bold text-green-600">₹{order.discountedPrice}</span>
                        </div>
                        <div className="text-xs text-gray-500">{order.discountPercentage}% off</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-green-600">₹{order.commissionEarned.toFixed(2)}</div>
                        {order.commissionPaid ? (
                          <span className="text-xs text-green-600">✓ Paid</span>
                        ) : order.status === 'completed' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-1 h-6 text-xs"
                            onClick={() => markCommissionAsPaid(order._id)}
                          >
                            Mark as Paid
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400">Not due</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(order.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => viewDetails(order)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages >= 1 && (
              <div className="px-6 py-4 border-t">
                <PaginationWrapper
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={limit}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Order Modal */}
      <CreatePromotionalOrderForm
        isOpen={showCreateOrderModal}
        onClose={() => setShowCreateOrderModal(false)}
        onSuccess={() => {
          setShowCreateOrderModal(false);
          fetchOrders();
          fetchStats();
        }}
      />

      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Promotional Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Campaign Name</p>
                  <p className="font-medium">{selectedOrder.campaignName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">View Count</p>
                  <p className="font-medium">{selectedOrder.viewCount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Valid Until</p>
                  <p className="font-medium">{formatDate(selectedOrder.validUntil)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Commission Rate</p>
                  <p className="font-medium">{selectedOrder.commissionPercentage}%</p>
                </div>
              </div>
              {selectedOrder.notes && (
                <div>
                  <p className="text-sm text-gray-500">Notes</p>
                  <p className="font-medium">{selectedOrder.notes}</p>
                </div>
              )}
              {selectedOrder.paymentId && (
                <div>
                  <p className="text-sm text-gray-500">Payment ID</p>
                  <p className="font-medium">{selectedOrder.paymentId}</p>
                </div>
              )}
              
              {/* Action Buttons */}
              {selectedOrder.status === 'pending' && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="destructive"
                    onClick={() => cancelOrder(selectedOrder._id)}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Order
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}